'use client';
import {
  FavouritesCreateDisclosure,
  FavouritesCreateDisclosureLabel,
  FavouritesCreateForm,
  FavouritesCreateFormVariant,
  FavouritesCreatePanel,
  FavouritesCreatePanelClose,
  FavouritesCreatePanelHeader,
  FavouritesCreatePanelTitle,
} from '@vs-shop/commerce-ui/favouritesCreateForm';
import { Button } from '@vs-shop/ui/button';
import { Input } from '@vs-shop/ui/input';
import { captureCause } from '../../../../shared/utils/sentry';
import { Exit } from 'effect';
import { PlusCircle, X } from 'lucide-react';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { showErrorToast, showSuccessToast } from '@vs-shop/ui/toast';

import effectTsResolver from '@vs-shop/engine/adapter/effect-hook-form/effectTsResolver';
import { createFavouriteListAtomFn } from '@vs-shop/engine/atom/favourites/createFavouriteListAtomFn';
import type { CreateFavouritesListFormData } from '@vs-shop/schemas/createFavouritesListForm';
import { CreateFavouritesListFormSchema } from '@vs-shop/schemas/createFavouritesListForm';
import { useAtomSet } from '@effect/atom-react';

export default function ProductFavouritesModalCreateNewList() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPending, startTransition] = useTransition();

  const intl = useIntl();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateFavouritesListFormData>({
    resolver: effectTsResolver(CreateFavouritesListFormSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
    },
  });

  const createFavouriteList = useAtomSet(createFavouriteListAtomFn, {
    mode: 'promiseExit',
  });

  const onSubmit = (data: CreateFavouritesListFormData) => {
    startTransition(async () => {
      const result = await createFavouriteList({
        name: data.name,
      });

      Exit.match(result, {
        onFailure: (cause) => {
          captureCause(cause, {
            component: 'ProductFavouritesModalCreateNewList',
            action: 'Create favourite list failed',
            additionalData: {
              name: data.name,
            },
          });

          showErrorToast(
            intl.formatMessage({
              id: 'productFavouritesModal.createList.error',
              defaultMessage: 'Nepodařilo se vytvořit seznam oblíbených produktů.',
            })
          );
        },
        onSuccess: (createdList) => {
          showSuccessToast(
            intl.formatMessage(
              {
                id: 'productFavouritesModal.createList.success',
                defaultMessage: 'Seznam "{name}" byl vytvořen.',
              },
              { name: createdList.name }
            )
          );

          setIsExpanded(false);
          reset();
        },
      });
    });
  };

  if (!isExpanded) {
    return (
      <FavouritesCreateDisclosure
        onClick={() => {
          setIsExpanded(true);
        }}
      >
        <PlusCircle className="size-5 shrink-0" />
        <FavouritesCreateDisclosureLabel>
          {intl.formatMessage({
            id: 'favourites.list.createNew',
            defaultMessage: 'Nový seznam',
          })}
        </FavouritesCreateDisclosureLabel>
      </FavouritesCreateDisclosure>
    );
  }

  return (
    <FavouritesCreatePanel>
      <FavouritesCreatePanelHeader>
        <FavouritesCreatePanelTitle>
          {intl.formatMessage({
            id: 'productFavouritesModal.createList.heading',
            defaultMessage: 'Vytvořit nový seznam',
          })}
        </FavouritesCreatePanelTitle>
        <FavouritesCreatePanelClose
          onClick={() => {
            setIsExpanded(false);
            reset();
          }}
          aria-label={intl.formatMessage({
            id: 'common.close',
            defaultMessage: 'Zavřít',
          })}
        >
          <X className="size-4" />
        </FavouritesCreatePanelClose>
      </FavouritesCreatePanelHeader>

      <FavouritesCreateForm
        variant={FavouritesCreateFormVariant.INLINE}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Input
          {...register('name')}
          id="name"
          label={intl.formatMessage({
            id: 'productFavouritesModal.createList.input.label',
            defaultMessage: 'Název',
          })}
          error={errors.name?.message ?? null}
          hasValue={!!watch('name')}
          required
          size="compact"
        />

        <Button
          type="submit"
          variant="primary"
          size="sm"
          loading={isPending}
          disabled={isPending}
          className="h-11 text-sm uppercase"
        >
          {intl.formatMessage({
            id: 'productFavouritesModal.createList.submit',
            defaultMessage: 'Vytvořit',
          })}
        </Button>
      </FavouritesCreateForm>
    </FavouritesCreatePanel>
  );
}
